import { ChangeEvent, FocusEvent, ForwardedRef, RefCallback, useCallback, useLayoutEffect, useRef } from 'react'

import { clampLength, InputMask } from 'core/helpers/masks'

type UseMaskedInputParams = {
  /** The mask to apply. When undefined the input is fully transparent. */
  mask?: InputMask
  /** The RAW stored value. May be undefined (uncontrolled usage). */
  value?: string
  /** Receives the next RAW value (separators are display-only). */
  onChange?: (value: string) => void
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  /** Explicit placeholder; wins over the mask's default. */
  placeholder?: string
  /** An optional forwarded ref, merged with the hook's internal one. */
  ref?: ForwardedRef<HTMLInputElement>
}

type MaskedInputProps = {
  ref: RefCallback<HTMLInputElement>
  value: string | undefined
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur: (e: FocusEvent<HTMLInputElement>) => void
  placeholder: string | undefined
}

type UseMaskedInputResult = {
  /** Props to spread onto the underlying `<input>`. */
  inputProps: MaskedInputProps
  /** The prefix the mask declares (e.g. `$` for currency), if any. */
  prefix: string | undefined
}

type PendingCaret = {
  /** The raw value the caret position was computed against. */
  raw: string
  /** Number of raw characters before the caret. */
  rawBeforeCaret: number
}

/**
 * The DOM-facing controller for an {@link InputMask}: change -> `sanitize` ->
 * raw `onChange`, display via `format`, `maxRawLength` enforcement,
 * `normalizeOnBlur`, mask-declared placeholder/prefix defaults, and caret
 * restoration after a reformat (anchored by raw-character count).
 *
 * Works against any input, not just TextInput: spread `inputProps` onto a bare
 * `<input>` (or TextArea) to get identical behavior.
 */
const useMaskedInput = ({
  mask,
  value,
  onChange,
  onBlur,
  placeholder,
  ref,
}: UseMaskedInputParams): UseMaskedInputResult => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const pendingCaret = useRef<PendingCaret | null>(null)

  const mergedRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [ref],
  )

  // Restores the caret after a reformat. React 18 writes the controlled value
  // to the DOM only AFTER layout effects (which resets the selection to the
  // end), so the display value is written here first: React's later
  // restoration then finds a matching DOM value and leaves the selection
  // alone. The pending position is applied at most once and only if the value
  // it was computed against is still current.
  useLayoutEffect(() => {
    const pending = pendingCaret.current
    if (pending === null) return
    pendingCaret.current = null

    const node = inputRef.current
    if (node && mask && pending.raw === value) {
      const position = mask.format(pending.raw.slice(0, pending.rawBeforeCaret)).length
      node.value = mask.format(pending.raw)
      node.setSelectionRange(position, position)
    }
  })

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const typed = e.target.value

    if (!mask) {
      onChange?.(typed)
      return
    }

    const sanitized = mask.sanitize(typed)
    // null = the mask rejects the whole edit: keep the previous value silently.
    if (sanitized === null) return

    const raw = mask.maxRawLength === undefined ? sanitized : clampLength(sanitized, mask.maxRawLength)
    const display = mask.format(raw)

    if (display !== typed && mask.caretBehavior !== 'end') {
      const caret = e.target.selectionStart ?? typed.length
      const rawBeforeCaret = Math.min(mask.sanitize(typed.slice(0, caret))?.length ?? 0, raw.length)

      if (raw === (value ?? '')) {
        // The raw value did not change (e.g. backspace over a separator), so no
        // re-render will follow; reformat the DOM in place before React's
        // controlled-input restoration, keeping the caret where the edit was.
        const position = mask.format(raw.slice(0, rawBeforeCaret)).length
        e.target.value = display
        e.target.setSelectionRange(position, position)
      } else {
        pendingCaret.current = { raw, rawBeforeCaret }
      }
    }

    onChange?.(raw)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (mask?.normalizeOnBlur) {
      const raw = mask.sanitize(e.target.value)
      if (raw !== null && raw !== '') {
        const normalized = mask.normalizeOnBlur(raw)
        if (normalized !== raw) {
          onChange?.(normalized)
        }
      }
    }

    return onBlur?.(e)
  }

  return {
    inputProps: {
      ref: mergedRef,
      // An undefined value stays undefined (uncontrolled), matching the
      // pre-refactor pass-through behavior.
      value: value == null || !mask ? value : mask.format(value),
      onChange: handleChange,
      onBlur: handleBlur,
      placeholder: placeholder ?? mask?.placeholder,
    },
    prefix: mask?.prefix,
  }
}

export default useMaskedInput
